import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
  createAction,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import {
  showCustomNotification,
  showError,
} from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  DashboardService,
  GetAccountWidgetLinksResponse,
  UpdateAccountWidgetLinksRequest,
  UpdateAccountWidgetMainLayoutRequest,
  GetAccountWidgetMainLayoutResponse,
  GetAccountWidgetMainLayoutQuery,
  GetAccountWidgetMainLayoutParams,
} from '@app/gen/dashboard';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { Entity } from '@app/types';

import { SettingsWithRootState } from '../../../slice';
import { SettingsState } from '../../settings.state';

const DELAY_DEFAULT_MS = 1000;

const delay = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(null), time);
  });

export const getAvailableWidgets = createAsyncThunk<
  GetAccountWidgetMainLayoutResponse,
  GetAccountWidgetMainLayoutQuery & GetAccountWidgetMainLayoutParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAvailableWidgets',
  async (
    { account_id, company_id },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getAccountWidgetMainLayout({
        params: {
          account_id,
        },
        ...(company_id && {
          query: {
            company_id,
          },
        }),
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateWidgetsLayout = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    id: string;
    companyId?: string;
    body: UpdateAccountWidgetMainLayoutRequest;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateWidgetsLayout',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const { id, companyId, body } = values;

      await service.updateAccountWidgetMainLayout({
        params: {
          account_id: id,
        },
        ...(companyId && {
          query: {
            company_id: companyId,
          },
        }),
        body: body,
      });

      await delay(DELAY_DEFAULT_MS);
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const resetWidgets = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    id: string;
    companyName: string;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/resetWidgets',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      await service.deleteCompanyWidgetsExceptionsFromAccount({
        params: {
          company_id: values.id,
        },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Компания больше не является исключением',
            subtitle: 'Компания',
            message: values.companyName,
            type: 'info',
          },
        ]),
      );
      await delay(DELAY_DEFAULT_MS);
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showError(
          'Не удалось сбросить настройки компании\nПопробуйте, пожалуйста, еще раз',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getWidgetLinks = createAsyncThunk<
  GetAccountWidgetLinksResponse,
  { widgetId: string; accountId: string; companyId?: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getWidgetLinks',
  async (getWidgetLinksDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);
      const result = await service.getAccountWidgetLinks({
        params: {
          widget_id: getWidgetLinksDto.widgetId,
          account_id: getWidgetLinksDto.accountId,
        },
        ...(getWidgetLinksDto.companyId && {
          query: {
            company_id: getWidgetLinksDto.companyId,
          },
        }),
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateWidgetLinks = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    params: { widgetId: string; accountId: string; companyId?: string };
    body: UpdateAccountWidgetLinksRequest;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateWidgetLinks',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);
      const { params, body } = values;
      await service.updateAccountWidgetLinks({
        params: {
          widget_id: params.widgetId,
          account_id: params.accountId,
        },
        ...(params.companyId && {
          query: {
            company_id: params.companyId,
          },
        }),
        body: body,
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Электронные сервисы',
            type: 'info',
          },
        ]),
      );
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const resetWidgetLinks = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    id: string;
    name: string;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/resetWidgetLinks',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);
      await service.resetWidgetContent({
        params: {
          widget_id: values.id,
        },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: values.name,
            type: 'info',
          },
        ]),
      );
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const clearWidgetLinks = createAction('widgetLinks/clear');

export const settingsDashboardReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAvailableWidgets.pending, (state) => {
    state.widgets.status = 'loading';
    state.widgets.isExceptionWidget = false;
    state.widgets.error = null;
  });
  builder.addCase(getAvailableWidgets.fulfilled, (state, { payload }) => {
    state.widgets.status = 'complete';
    state.widgets.isExceptionWidget = !!payload?.is_exception_from_account;
    state.widgets.canResetLayout = !!payload?.has_account_widgets;
    state.widgets.items = payload.widgets || [];
  });
  builder.addCase(getAvailableWidgets.rejected, (state, { payload, error }) => {
    state.widgets.status = 'failed';
    state.widgets.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(getWidgetLinks.pending, (state) => {
    state.widgetLinks.status = 'loading';
    state.widgetLinks.error = null;
  });
  builder.addCase(getWidgetLinks.fulfilled, (state, { payload }) => {
    state.widgetLinks.status = 'complete';
    state.widgetLinks.isException = !!payload?.company_content_id;
    state.widgetLinks.items = payload.links || [];
  });
  builder.addCase(getWidgetLinks.rejected, (state, { payload, error }) => {
    state.widgetLinks.status = 'failed';
    state.widgetLinks.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(clearWidgetLinks, (state) => {
    state.widgetLinks.status = 'idle';
    state.widgetLinks.error = null;
    state.widgetLinks.isException = false;
    state.widgetLinks.items = [];
  });
};
